import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '~/shadcn/components/ui/Dialog';
import { Button } from '~/shadcn/components/ui/Button';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import Stepper from './components/Stepper';
import PrepStep1 from './components/PrepStep1';
import PrepStep2 from './components/PrepStep2';
import PrepStep3 from './components/PrepStep3';
import PrepStep4 from './components/PrepStep4';
import HighVarianceDialog from './components/HighVarianceDialog';
import CloseConfirmationDialog from './components/CloseConfirmationDialog';
import { usePrepPalletModal } from './hooks/usePrepPalletModal';

/**
 * Main modal component for the multi-step pallet prep process.
 *
 * @param {object} props
 * @param {boolean} props.isOpen - Whether the modal is open.
 * @param {function} props.onOpenChange - Function to handle modal open/close state changes.
 * @param {object} props.palletData - Data for the pallet being prepped (e.g., palletId, lotId).
 * @param {array} props.scrapReasons - Array of scrap reason options.
 * @returns {JSX.Element}
 */
function PrepPalletModal({ isOpen, onOpenChange, palletData, scrapReasons, onSuccess }) {
    const {
        // State
        currentStep,
        formData,
        isStepValid,
        confirmationRequired,
        showVarianceConfirmation,
        showCloseConfirmation,
        varianceDetails,
        isSubmitting,
        newLotId,
        newPallets,
        destinationLocations,

        // State setters
        setFormData,
        setIsStepValid,
        setConfirmationRequired,
        setVarianceDetails,
        setShowVarianceConfirmation,
        setShowCloseConfirmation,

        // Handlers
        handleNext,
        handleBack,
        handleConfirmPrep,
        handleOpenChange,
        handleCloseConfirm,
        proceedToNextStep,
        shouldShowCloseConfirmation,
    } = usePrepPalletModal(palletData, scrapReasons, onOpenChange, isOpen, onSuccess);

    const renderStep = () => {
        switch (currentStep) {
            case 1:
                return (
                    <PrepStep1
                        palletData={palletData}
                        formData={formData}
                        onDataChange={setFormData}
                        onValidationChange={setIsStepValid}
                        prepScrapReasons={scrapReasons}
                        destinationLocations={destinationLocations}
                    />
                );
            case 2:
                return (
                    <PrepStep2
                        palletData={palletData}
                        formData={formData}
                        onDataChange={setFormData}
                        onValidationChange={setIsStepValid}
                        setConfirmationRequired={setConfirmationRequired}
                        setVarianceDetails={setVarianceDetails}
                    />
                );
            case 3:
                return (
                    <PrepStep3
                        palletData={palletData}
                        formData={formData}
                        isHighVariance={confirmationRequired}
                        varianceDetails={varianceDetails}
                    />
                );
            case 4:
                return <PrepStep4 newPallets={newPallets} newLotId={newLotId} />;
            default:
                return null;
        }
    };

    return (
        <>
            <Dialog open={isOpen} onOpenChange={handleOpenChange}>
                <DialogContent
                    className="sm:max-w-4xl p-0"
                    onEscapeKeyDown={(e) => {
                        if (shouldShowCloseConfirmation()) {
                            e.preventDefault();
                            setShowCloseConfirmation(true);
                        }
                    }}
                    onPointerDownOutside={(e) => {
                        if (shouldShowCloseConfirmation()) {
                            e.preventDefault(); // Just ignore the click, no confirmation
                        }
                    }}
                >
                    <DialogHeader className="px-6 pt-4 pb-0 bg-gray-50 rounded-t-lg border-b gap-0">
                        <DialogTitle className="sr-only">
                            Prep Lot {palletData?.lotId} - Step {currentStep}
                        </DialogTitle>
                        <DialogDescription className="sr-only">
                            Prep Pallet {palletData?.palletId} from lot {palletData?.lotId}
                        </DialogDescription>
                        <p className="text-xl text-left">Prep Lot {palletData?.lotId}</p>
                        <p className="text-xs text-muted-foreground text-left">Pallet: {palletData?.palletId}</p>
                        <p className="text-xs text-muted-foreground text-left">
                            Current Location: {palletData?.locationName}
                        </p>
                        <Stepper currentStep={currentStep} />
                    </DialogHeader>

                    <div className="py-0 px-3">{renderStep()}</div>

                    <DialogFooter className="px-6 py-4 bg-gray-50/50 rounded-b-lg border-t sm:justify-between">
                        <div>
                            {currentStep > 1 && currentStep < 4 && (
                                <Button variant="outline" onClick={handleBack} disabled={isSubmitting}>
                                    <ChevronLeft className="h-4 w-4 mr-2" />
                                    Back
                                </Button>
                            )}
                        </div>
                        <div>
                            {currentStep < 3 && (
                                <Button
                                    onClick={handleNext}
                                    disabled={!isStepValid}
                                    className="bg-blue-600 disabled:bg-gray-500"
                                >
                                    Next
                                    <ChevronRight className="h-4 w-4 ml-2" />
                                </Button>
                            )}
                            {currentStep === 3 && (
                                <Button
                                    onClick={handleConfirmPrep}
                                    disabled={isSubmitting || !isStepValid}
                                    className="bg-blue-600 disabled:bg-gray-500"
                                >
                                    {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm'}
                                </Button>
                            )}
                            {currentStep === 4 && (
                                <Button
                                    className="bg-green-600 disabled:bg-gray-500"
                                    onClick={() => handleOpenChange(false)}
                                >
                                    Close
                                </Button>
                            )}
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <HighVarianceDialog
                open={showVarianceConfirmation}
                onOpenChange={setShowVarianceConfirmation}
                onConfirm={proceedToNextStep}
                variance={varianceDetails.variancePercent}
                expected={varianceDetails.expected}
                userDeclaredTotal={varianceDetails.userDeclaredTotal}
            />

            <CloseConfirmationDialog
                open={showCloseConfirmation}
                onOpenChange={setShowCloseConfirmation}
                onConfirm={handleCloseConfirm}
            />
        </>
    );
}

export default PrepPalletModal;
